import React, { useState } from "react";
import { useLanguage } from "./LanguageContext";
import InterestModal from "./InterestModal";
import { usePageMeta } from "../hooks/usePageMeta";

const pageStyles = `
  .fp-root {
    font-family: 'DM Sans', sans-serif;
    color: #0f172a;
    background: #f8fafc;
    min-height: 100vh;
  }

  /* ── Hero ── */
  .fp-hero {
    background: white;
    border-bottom: 1px solid #e2e8f0;
    padding: 80px 40px 72px;
    text-align: center;
  }

  .fp-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 12px;
    font-weight: 600;
    color: #2563eb;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin-bottom: 24px;
    padding: 5px 14px;
    background: rgba(37,99,235,0.07);
    border-radius: 100px;
    border: 1px solid rgba(37,99,235,0.15);
  }

  .fp-hero-title {
    font-family: 'new-science','DM Sans',sans-serif;
    font-size: clamp(32px,5vw,52px);
    color: #0f172a;
    line-height: 1.1;
    letter-spacing: -0.02em;
    margin: 0 0 20px;
  }

  .fp-hero-title em { color: #2563eb; font-style: italic; }

  .fp-hero-lead {
    font-size: 17px;
    color: #64748b;
    line-height: 1.65;
    max-width: 580px;
    margin: 0 auto 36px;
  }

  .fp-hero-cta {
    display: flex;
    justify-content: center;
    gap: 12px;
    flex-wrap: wrap;
  }

  /* ── Sections ── */
  .fp-section {
    max-width: 1040px;
    margin: 0 auto;
    padding: 64px 40px;
  }

  .fp-section + .fp-section {
    padding-top: 0;
  }

  .fp-section-alt {
    background: white;
    border-top: 1px solid #e2e8f0;
    border-bottom: 1px solid #e2e8f0;
  }

  .fp-section-inner {
    max-width: 1040px;
    margin: 0 auto;
    padding: 64px 40px;
  }

  .fp-section-label {
    font-size: 12px;
    font-weight: 700;
    color: #2563eb;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin-bottom: 12px;
  }

  .fp-section-title {
    font-family: 'new-science','DM Sans',sans-serif;
    font-size: 28px;
    color: #0f172a;
    margin: 0 0 12px;
    letter-spacing: -0.01em;
  }

  .fp-section-body {
    font-size: 15px;
    color: #64748b;
    line-height: 1.65;
    max-width: 680px;
    margin: 0 0 32px;
  }

  /* ── Cards ── */
  .fp-grid-2 {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  .fp-grid-3 {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }

  .fp-grid-4 {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
  }

  .fp-grid-5 {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 16px;
  }

  .fp-card {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 24px 22px;
    transition: box-shadow 0.2s, transform 0.2s;
  }

  .fp-card:hover {
    box-shadow: 0 8px 28px rgba(0,0,0,0.07);
    transform: translateY(-2px);
  }

  .fp-card-icon {
    font-size: 22px;
    margin-bottom: 12px;
  }

  .fp-card-title {
    font-size: 14.5px;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 7px;
  }

  .fp-card-body {
    font-size: 13.5px;
    color: #64748b;
    line-height: 1.6;
    margin: 0;
  }

  /* ── Steps ── */
  .fp-steps {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .fp-step {
    display: flex;
    gap: 16px;
    align-items: flex-start;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 16px 20px;
  }

  .fp-step-num {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: #2563eb;
    color: white;
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 1px;
  }

  .fp-step-title { font-size: 14px; font-weight: 600; color: #0f172a; margin-bottom: 3px; }
  .fp-step-sub { font-size: 13px; color: #64748b; }

  /* ── Metrics strip ── */
  .fp-metrics {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 28px;
  }

  .fp-metric-badge {
    display: flex;
    align-items: center;
    gap: 7px;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 7px 13px;
    font-size: 13px;
    color: #0f172a;
    font-weight: 500;
  }

  .fp-metric-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }

  /* ── Tip ── */
  .fp-tip {
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    border-radius: 10px;
    padding: 16px 20px;
    margin-top: 20px;
    display: flex;
    gap: 10px;
    align-items: flex-start;
    font-size: 13.5px;
    color: #1e40af;
    line-height: 1.6;
  }

  /* ── Sentiment cards ── */
  .fp-sentiment-grid {
    display: grid;
    grid-template-columns: repeat(3,1fr);
    gap: 16px;
    margin-bottom: 20px;
  }

  /* ── Table ── */
  .fp-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13.5px;
    background: white;
    border-radius: 10px;
    overflow: hidden;
    border: 1px solid #e2e8f0;
  }

  .fp-table th {
    background: #f8fafc;
    padding: 12px 16px;
    text-align: left;
    font-size: 12px;
    font-weight: 700;
    color: #64748b;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    border-bottom: 1px solid #e2e8f0;
  }

  .fp-table td {
    padding: 14px 16px;
    color: #334155;
    border-bottom: 1px solid #f1f5f9;
    line-height: 1.5;
    vertical-align: top;
  }

  .fp-table tr:last-child td { border-bottom: none; }
  .fp-table td:first-child { font-weight: 600; color: #0f172a; white-space: nowrap; }

  /* ── CTA band ── */
  .fp-cta-band {
    background: linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%);
    padding: 64px 40px;
    text-align: center;
  }

  .fp-cta-band h2 {
    font-family: 'new-science','DM Sans',sans-serif;
    font-size: 28px;
    color: white;
    margin: 0 0 12px;
  }

  .fp-cta-band p {
    font-size: 15px;
    color: rgba(255,255,255,0.65);
    margin: 0 0 28px;
    max-width: 480px;
    margin-left: auto;
    margin-right: auto;
  }

  /* ── Buttons ── */
  .fp-btn-primary {
    padding: 13px 26px;
    border-radius: 9px;
    border: none;
    background: #2563eb;
    color: white;
    font-family: 'DM Sans',sans-serif;
    font-size: 14.5px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 4px 14px rgba(37,99,235,0.3);
    transition: background 0.2s, transform 0.1s;
  }

  .fp-btn-primary:hover { background: #1d4ed8; transform: translateY(-1px); }

  .fp-btn-ghost {
    padding: 13px 26px;
    border-radius: 9px;
    border: 1.5px solid #e2e8f0;
    background: white;
    color: #0f172a;
    font-family: 'DM Sans',sans-serif;
    font-size: 14.5px;
    font-weight: 500;
    cursor: pointer;
    transition: border-color 0.2s;
  }

  .fp-btn-ghost:hover { border-color: #94a3b8; }

  .fp-btn-white {
    padding: 13px 26px;
    border-radius: 9px;
    border: none;
    background: white;
    color: #0f172a;
    font-family: 'DM Sans',sans-serif;
    font-size: 14.5px;
    font-weight: 700;
    cursor: pointer;
    box-shadow: 0 4px 14px rgba(0,0,0,0.15);
  }

  /* ── Page-type grid ── */
  .fp-type-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
  }

  .fp-type-chip {
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 13px;
    font-weight: 600;
    color: #334155;
    text-align: center;
  }

  @media (max-width: 768px) {
    .fp-grid-2, .fp-grid-3, .fp-grid-4, .fp-grid-5 { grid-template-columns: 1fr; }
    .fp-sentiment-grid { grid-template-columns: 1fr; }
    .fp-type-grid { grid-template-columns: repeat(2,1fr); }
    .fp-hero { padding: 56px 20px 48px; }
    .fp-section, .fp-section-inner { padding: 40px 20px; }
    .fp-cta-band { padding: 48px 20px; }
  }
`;

// ─── shared CTA band ─────────────────────────────────────────────────────────
function CtaBand({ onSignup }) {
  const { t } = useLanguage();
  return (
    <div className="fp-cta-band">
      <h2>{t("fp.ctaTitle")}</h2>
      <p>{t("fp.ctaSub")}</p>
      <button className="fp-btn-white" onClick={onSignup}>
        {t("fp.ctaBtn")}
      </button>
    </div>
  );
}

// ─── shared hero ─────────────────────────────────────────────────────────────
function FpHero({ eyebrow, title1, title2, lead }) {
  return (
    <div className="fp-hero">
      <div style={{ maxWidth: 700, margin: "0 auto" }}>
        <div className="fp-eyebrow">
          <span style={{ width: 5, height: 5, background: "#2563eb", borderRadius: "50%", display: "inline-block" }} />
          {eyebrow}
        </div>
        <h1 className="fp-hero-title">{title1}<br /><em>{title2}</em></h1>
        <p className="fp-hero-lead">{lead}</p>
      </div>
    </div>
  );
}

// ─── 1. Visibility Plattform ─────────────────────────────────────────────────
export function VisibilityPlattformPage({ onSignup }) {
  const { t } = useLanguage();
  usePageMeta({ title: `${t("publicNav.methode")} — GeoRev.ai` });
  const metColors = ["#2563eb","#7c3aed","#16a34a","#ea580c","#0891b2","#d97706","#dc2626"];
  return (
    <div className="fp-root">
      <style>{pageStyles}</style>
      <FpHero eyebrow={t("visibility.eyebrow")} title1={t("visibility.heroTitle1")} title2={t("visibility.heroTitle2")} lead={t("visibility.heroLead")} />

      <div className="fp-section">
        <div className="fp-section-label">{t("visibility.howLabel")}</div>
        <h2 className="fp-section-title">{t("visibility.howTitle")}</h2>
        <p className="fp-section-body">{t("visibility.howBody")}</p>
        <div className="fp-steps">
          {[1,2,3,4,5].map(i => (
            <div className="fp-step" key={i}>
              <div className="fp-step-num">{i}</div>
              <div>
                <div className="fp-step-title">{t(`visibility.s${i}t`)}</div>
                <div className="fp-step-sub">{t(`visibility.s${i}s`)}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="fp-tip"><span style={{fontSize:16}}>💡</span><span><strong>{t("fp.tipLabel")}</strong> {t("visibility.tip")}</span></div>
      </div>

      <div className="fp-section-alt">
        <div className="fp-section-inner">
          <div className="fp-section-label">{t("visibility.conLabel")}</div>
          <h2 className="fp-section-title">{t("visibility.conTitle")}</h2>
          <div className="fp-grid-3">
            {[
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>,
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9"/><path d="M7.8 16.2C5.3 13.7 5.3 9.8 7.8 7.3"/><circle cx="12" cy="12" r="2"/><path d="M16.2 7.8c2.5 2.5 2.5 6.4 0 8.9"/><path d="M19.1 4.9C23 8.8 23 15.2 19.1 19.1"/></svg>,
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/></svg>,
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
            ].map((icon, i) => {
              const n = i + 1;
              return (
                <div className="fp-card" key={n}>
                  <div className="fp-card-icon">{icon}</div>
                  <div className="fp-card-title">{t(`visibility.c${n}t`)}</div>
                  <p className="fp-card-body">{t(`visibility.c${n}d`)}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="fp-section">
        <div className="fp-section-label">{t("visibility.metLabel")}</div>
        <h2 className="fp-section-title">{t("visibility.metTitle")}</h2>
        <p className="fp-section-body">{t("visibility.metBody")}</p>
        <div className="fp-metrics">
          {[1,2,3,4,5,6,7].map(i => (
            <div className="fp-metric-badge" key={i}>
              <div className="fp-metric-dot" style={{background: metColors[i-1]}} />{t(`visibility.met${i}`)}
            </div>
          ))}
        </div>
        <div className="fp-grid-2">
          {[1,2,3,4,5,6].map(i => (
            <div className="fp-card" key={i}>
              <div className="fp-card-title">{t(`visibility.mc${i}t`)}</div>
              <p className="fp-card-body">{t(`visibility.mc${i}d`)}</p>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}

// ─── 2. Ranking Analyse ──────────────────────────────────────────────────────
export function RankingAnalysePage({ onSignup }) {
  const { t } = useLanguage();
  return (
    <div className="fp-root">
      <style>{pageStyles}</style>
      <FpHero eyebrow={t("ranking.eyebrow")} title1={t("ranking.heroTitle1")} title2={t("ranking.heroTitle2")} lead={t("ranking.heroLead")} />

      <div className="fp-section">
        <div className="fp-section-label">{t("ranking.posLabel")}</div>
        <h2 className="fp-section-title">{t("ranking.posTitle")}</h2>
        <p className="fp-section-body">{t("ranking.posBody")}</p>
        <div className="fp-grid-3">
          {[
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>,
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="6"/><path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11"/></svg>,
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 21 3 4"/><polyline points="3 21 21 21"/><polyline points="5 17 9 12 13 15 19 7"/></svg>,
          ].map((icon, i) => (
            <div className="fp-card" key={i+1}>
              <div className="fp-card-icon">{icon}</div>
              <div className="fp-card-title">{t(`ranking.p${i+1}t`)}</div>
              <p className="fp-card-body">{t(`ranking.p${i+1}d`)}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="fp-section-alt">
        <div className="fp-section-inner">
          <div className="fp-section-label">{t("ranking.engLabel")}</div>
          <h2 className="fp-section-title">{t("ranking.engTitle")}</h2>
          <p className="fp-section-body">{t("ranking.engBody")}</p>
          <div className="fp-grid-5">
            {[
              { key: 1, logo: (
                <svg width="28" height="28" viewBox="-2 -2 28 28" fill="#2563eb">
                  <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z"/>
                </svg>
              )},
              { key: 2, logo: (
                <svg width="28" height="28" viewBox="-2 -2 28 28" fill="#2563eb">
                  <path d="m4.7144 15.9555 4.7174-2.6471.079-.2307-.079-.1275h-.2307l-.7893-.0486-2.6956-.0729-2.3375-.0971-2.2646-.1214-.5707-.1215-.5343-.7042.0546-.3522.4797-.3218.686.0608 1.5179.1032 2.2767.1578 1.6514.0972 2.4468.255h.3886l.0546-.1579-.1336-.0971-.1032-.0972L6.973 9.8356l-2.55-1.6879-1.3356-.9714-.7225-.4918-.3643-.4614-.1578-1.0078.6557-.7225.8803.0607.2246.0607.8925.686 1.9064 1.4754 2.4893 1.8336.3643.3035.1457-.1032.0182-.0728-.164-.2733-1.3539-2.4467-1.445-2.4893-.6435-1.032-.17-.6194c-.0607-.255-.1032-.4674-.1032-.7285L6.287.1335 6.6997 0l.9957.1336.419.3642.6192 1.4147 1.0018 2.2282 1.5543 3.0296.4553.8985.2429.8318.091.255h.1579v-.1457l.1275-1.706.2368-2.0947.2307-2.6957.0789-.7589.3764-.9107.7468-.4918.5828.2793.4797.686-.0668.4433-.2853 1.8517-.5586 2.9021-.3643 1.9429h.2125l.2429-.2429.9835-1.3053 1.6514-2.0643.7286-.8196.85-.9046.5464-.4311h1.0321l.759 1.1293-.34 1.1657-1.0625 1.3478-.8804 1.1414-1.2628 1.7-.7893 1.36.0729.1093.1882-.0183 2.8535-.607 1.5421-.2794 1.8396-.3157.8318.3886.091.3946-.3278.8075-1.967.4857-2.3072.4614-3.4364.8136-.0425.0304.0486.0607 1.5482.1457.6618.0364h1.621l3.0175.2247.7892.522.4736.6376-.079.4857-1.2142.6193-1.6393-.3886-3.825-.9107-1.3113-.3279h-.1822v.1093l1.0929 1.0686 2.0035 1.8092 2.5075 2.3314.1275.5768-.3218.4554-.34-.0486-2.2039-1.6575-.85-.7468-1.9246-1.621h-.1275v.17l.4432.6496 2.3436 3.5214.1214 1.0807-.17.3521-.6071.2125-.6679-.1214-1.3721-1.9246L14.38 17.959l-1.1414-1.9428-.1397.079-.674 7.2552-.3156.3703-.7286.2793-.6071-.4614-.3218-.7468.3218-1.4753.3886-1.9246.3157-1.53.2853-1.9004.17-.6314-.0121-.0425-.1397.0182-1.4328 1.9672-2.1796 2.9446-1.7243 1.8456-.4128.164-.7164-.3704.0667-.6618.4008-.5889 2.386-3.0357 1.4389-1.882.929-1.0868-.0062-.1579h-.0546l-6.3385 4.1164-1.1293.1457-.4857-.4554.0608-.7467.2307-.2429 1.9064-1.3114Z"/>
                </svg>
              )},
              { key: 3, logo: (
                <svg width="28" height="28" viewBox="-2 -2 28 28" fill="#2563eb">
                  <path d="M22.3977 7.0896h-2.3106V.0676l-7.5094 6.3542V.1577h-1.1554v6.1966L4.4904 0v7.0896H1.6023v10.3976h2.8882V24l6.932-6.3591v6.2005h1.1554v-6.0469l6.9318 6.1807v-6.4879h2.8882V7.0896zm-3.4657-4.531v4.531h-5.355l5.355-4.531zm-13.2862.0676 4.8691 4.4634H5.6458V2.6262zM2.7576 16.332V8.245h7.8476l-6.1149 6.1147v1.9723H2.7576zm2.8882 5.0404v-3.8852h.0001v-2.6488l5.7763-5.7764v7.0111l-5.7764 5.2993zm12.7086.0248-5.7766-5.1509V9.0618l5.7766 5.7766v6.5588zm2.8882-5.0652h-1.733v-1.9723L13.3948 8.245h7.8478v8.087z"/>
                </svg>
              )},
              { key: 4, logo: (
                <svg width="28" height="28" viewBox="1 1 22 22" fill="#2563eb">
                  <path d="M12 2c0 5.5-4.5 10-10 10 5.5 0 10 4.5 10 10 0-5.5 4.5-10 10-10-5.5 0-10-4.5-10-10z"/>
                </svg>
              )},
              { key: 5, logo: (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="#2563eb">
                  <path fillRule="evenodd" d="M4.5 1H11a3.5 3.5 0 0 1 3.5 3.5V11A3.5 3.5 0 0 1 11 14.5H4.5A3.5 3.5 0 0 1 1 11V4.5A3.5 3.5 0 0 1 4.5 1ZM13 9.5h6.5A3.5 3.5 0 0 1 23 13v6.5A3.5 3.5 0 0 1 19.5 23H13a3.5 3.5 0 0 1-3.5-3.5V13A3.5 3.5 0 0 1 13 9.5Z"/>
                </svg>
              )},
            ].map(({ key, logo }) => (
              <div className="fp-card" key={key}>
                <div className="fp-card-icon">{logo}</div>
                <div className="fp-card-title">{t(`ranking.e${key}t`)}</div>
                <p className="fp-card-body">{t(`ranking.e${key}d`)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="fp-section">
        <div className="fp-section-label">{t("ranking.dshLabel")}</div>
        <h2 className="fp-section-title">{t("ranking.dshTitle")}</h2>
        <div className="fp-grid-2">
          {[1,2,3,4].map(i => (
            <div className="fp-card" key={i}>
              <div className="fp-card-title">{t(`ranking.d${i}t`)}</div>
              <p className="fp-card-body">{t(`ranking.d${i}d`)}</p>
            </div>
          ))}
        </div>
        <div className="fp-tip"><span style={{fontSize:16}}>💡</span><span><strong>{t("fp.tipLabel")}</strong> {t("ranking.tip")}</span></div>
      </div>

    </div>
  );
}

// ─── 3. Sentiment Analyse ────────────────────────────────────────────────────
export function SentimentAnalysePage({ onSignup }) {
  const { t } = useLanguage();
  return (
    <div className="fp-root">
      <style>{pageStyles}</style>
      <FpHero eyebrow={t("sentiment.eyebrow")} title1={t("sentiment.heroTitle1")} title2={t("sentiment.heroTitle2")} lead={t("sentiment.heroLead")} />

      <div className="fp-section">
        <div className="fp-section-label">{t("sentiment.catLabel")}</div>
        <h2 className="fp-section-title">{t("sentiment.catTitle")}</h2>
        <p className="fp-section-body">{t("sentiment.catBody")}</p>
        <div className="fp-sentiment-grid">
          <div className="fp-card" style={{borderTop:"3px solid #16a34a"}}>
            <div className="fp-card-icon"><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M8 13s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg></div>
            <div className="fp-card-title" style={{color:"#16a34a"}}>{t("sentiment.posTitle")}</div>
            <p className="fp-card-body">{t("sentiment.posDesc")}</p>
          </div>
          <div className="fp-card" style={{borderTop:"3px solid #64748b"}}>
            <div className="fp-card-icon"><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="8" y1="13" x2="16" y2="13"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg></div>
            <div className="fp-card-title" style={{color:"#64748b"}}>{t("sentiment.neuTitle")}</div>
            <p className="fp-card-body">{t("sentiment.neuDesc")}</p>
          </div>
          <div className="fp-card" style={{borderTop:"3px solid #dc2626"}}>
            <div className="fp-card-icon"><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M16 16s-1.5-2-4-2-4 2-4 2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></svg></div>
            <div className="fp-card-title" style={{color:"#dc2626"}}>{t("sentiment.negTitle")}</div>
            <p className="fp-card-body">{t("sentiment.negDesc")}</p>
          </div>
        </div>
        <div className="fp-tip"><span style={{fontSize:16}}>💡</span><span><strong>{t("fp.tipLabel")}</strong> {t("sentiment.tip")}</span></div>
      </div>

      <div className="fp-section-alt">
        <div className="fp-section-inner">
          <div className="fp-section-label">{t("sentiment.whyLabel")}</div>
          <h2 className="fp-section-title">{t("sentiment.whyTitle")}</h2>
          <p className="fp-section-body">{t("sentiment.whyBody")}</p>
          <div className="fp-grid-3">
            {[
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>,
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"/><path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"/><path d="M7 21h10"/><path d="M12 3v18"/><path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2"/></svg>,
            ].map((icon, i) => (
              <div className="fp-card" key={i+1}>
                <div className="fp-card-icon">{icon}</div>
                <div className="fp-card-title">{t(`sentiment.why${i+1}t`)}</div>
                <p className="fp-card-body">{t(`sentiment.why${i+1}d`)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="fp-section">
        <div className="fp-section-label">{t("sentiment.dshLabel")}</div>
        <h2 className="fp-section-title">{t("sentiment.dshTitle")}</h2>
        <div className="fp-grid-2">
          {[1,2,3,4].map(i => (
            <div className="fp-card" key={i}>
              <div className="fp-card-title">{t(`sentiment.ds${i}t`)}</div>
              <p className="fp-card-body">{t(`sentiment.ds${i}d`)}</p>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}

// ─── 4. Quellenangabe Analyse ─────────────────────────────────────────────────
export function QuellenangabeAnalysePage({ onSignup }) {
  const { t } = useLanguage();
  return (
    <div className="fp-root">
      <style>{pageStyles}</style>
      <FpHero eyebrow={t("citations.eyebrow")} title1={t("citations.heroTitle1")} title2={t("citations.heroTitle2")} lead={t("citations.heroLead")} />

      <div className="fp-section">
        <div className="fp-section-label">{t("citations.meanLabel")}</div>
        <h2 className="fp-section-title">{t("citations.meanTitle")}</h2>
        <p className="fp-section-body">{t("citations.meanBody")}</p>
        <div className="fp-grid-2">
          {[
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><polyline points="8 12 11 15 16 9"/></svg>,
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>,
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>,
          ].map((icon, i) => (
            <div className="fp-card" key={i+1}>
              <div className="fp-card-icon">{icon}</div>
              <div className="fp-card-title">{t(`citations.m${i+1}t`)}</div>
              <p className="fp-card-body">{t(`citations.m${i+1}d`)}</p>
            </div>
          ))}
        </div>
        <div className="fp-tip"><span style={{fontSize:16}}>💡</span><span><strong>{t("fp.tipLabel")}</strong> {t("citations.tip")}</span></div>
      </div>

      <div className="fp-section-alt">
        <div className="fp-section-inner">
          <div className="fp-section-label">{t("citations.engLabel")}</div>
          <h2 className="fp-section-title">{t("citations.engTitle")}</h2>
          <p className="fp-section-body">{t("citations.engBody")}</p>
          <table className="fp-table">
            <thead>
              <tr><th>{t("citations.tEngine")}</th><th>{t("citations.tBehav")}</th><th>{t("citations.tRel")}</th></tr>
            </thead>
            <tbody>
              {[1,2,3,4].map(i => (
                <tr key={i}><td>{t(`citations.tb${i}a`)}</td><td>{t(`citations.tb${i}b`)}</td><td>{t(`citations.tb${i}c`)}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="fp-section">
        <div className="fp-section-label">{t("citations.ucLabel")}</div>
        <h2 className="fp-section-title">{t("citations.ucTitle")}</h2>
        <div className="fp-grid-3">
          {[
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>,
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><polyline points="9 16 11 18 15 14"/></svg>,
          ].map((icon, i) => (
            <div className="fp-card" key={i+1}>
              <div className="fp-card-icon">{icon}</div>
              <div className="fp-card-title">{t(`citations.uc${i+1}t`)}</div>
              <p className="fp-card-body">{t(`citations.uc${i+1}d`)}</p>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}

// ─── 5. Webseiten Audit ──────────────────────────────────────────────────────
export function WebseitenAuditPage({ onSignup }) {
  const { t } = useLanguage();
  usePageMeta({ title: `${t("publicNav.websiteAudit")} — GeoRev.ai` });
  const pageTypes = ["Local Business","YMYL (Your Money, Your Life)","E-Commerce","Blog / Editorial","News","Corporate","Landing Page","Support / FAQ"];
  return (
    <div className="fp-root">
      <style>{pageStyles}</style>
      <FpHero eyebrow={t("audit.eyebrow")} title1={t("audit.heroTitle1")} title2={t("audit.heroTitle2")} lead={t("audit.heroLead")} />

      <div className="fp-section">
        <div className="fp-section-label">{t("audit.howLabel")}</div>
        <h2 className="fp-section-title">{t("audit.howTitle")}</h2>
        <p className="fp-section-body">{t("audit.howBody")}</p>
        <div className="fp-steps">
          {[1,2,3,4].map(i => (
            <div className="fp-step" key={i}>
              <div className="fp-step-num">{i}</div>
              <div>
                <div className="fp-step-title">{t(`audit.as${i}t`)}</div>
                <div className="fp-step-sub">{t(`audit.as${i}s`)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="fp-section-alt">
        <div className="fp-section-inner">
          <div className="fp-section-label">{t("audit.typeLabel")}</div>
          <h2 className="fp-section-title">{t("audit.typeTitle")}</h2>
          <p className="fp-section-body">{t("audit.typeBody")}</p>
          <div className="fp-type-grid">
            {pageTypes.map(pt => <div className="fp-type-chip" key={pt}>{pt}</div>)}
          </div>
        </div>
      </div>

      <div className="fp-section">
        <div className="fp-section-label">{t("audit.evalLabel")}</div>
        <h2 className="fp-section-title">{t("audit.evalTitle")}</h2>
        <p className="fp-section-body">{t("audit.evalBody")}</p>
        <table className="fp-table">
          <thead><tr><th>{t("audit.tArea")}</th><th>{t("audit.tMeas")}</th></tr></thead>
          <tbody>
            {[1,2,3,4,5,6,7].map(i => (
              <tr key={i}><td>{t(`audit.aa${i}n`)}</td><td>{t(`audit.aa${i}d`)}</td></tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="fp-section-alt">
        <div className="fp-section-inner">
          <div className="fp-section-label">{t("audit.whyLabel")}</div>
          <h2 className="fp-section-title">{t("audit.whyTitle")}</h2>
          <div className="fp-grid-3">
            {[
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>,
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="6" x2="20" y2="6"/><circle cx="14" cy="6" r="2.5" fill="#2563eb" stroke="#2563eb"/><line x1="4" y1="12" x2="20" y2="12"/><circle cx="8" cy="12" r="2.5" fill="#2563eb" stroke="#2563eb"/><line x1="4" y1="18" x2="20" y2="18"/><circle cx="16" cy="18" r="2.5" fill="#2563eb" stroke="#2563eb"/></svg>,
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="14" r="8"/><polyline points="12 10 12 14 15 16"/><line x1="10" y1="2" x2="14" y2="2"/><line x1="12" y1="2" x2="12" y2="6"/><line x1="19" y1="5" x2="21" y2="7"/></svg>,
            ].map((icon, i) => (
              <div className="fp-card" key={i+1}>
                <div className="fp-card-icon">{icon}</div>
                <div className="fp-card-title">{t(`audit.aw${i+1}t`)}</div>
                <p className="fp-card-body">{t(`audit.aw${i+1}d`)}</p>
              </div>
            ))}
          </div>
          <div className="fp-tip" style={{marginTop:24}}>
            <span style={{fontSize:16}}>💡</span>
            <span><strong>{t("audit.tipTitle")}</strong> {t("audit.tip")}</span>
          </div>
        </div>
      </div>

    </div>
  );
}

// ─── 6. Beratung und Workshops ───────────────────────────────────────────────
export function BeratungPage({ onSignup }) {
  const { t } = useLanguage();
  usePageMeta({ title: `${t("publicNav.beratung")} — GeoRev.ai` });
  const [showModal, setShowModal] = useState(false);

  const offerings = [
    {
      icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
      name: t("beratung.o1name"), sub: t("beratung.o1sub"), desc: t("beratung.o1d"),
    },
    {
      icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>,
      name: t("beratung.o2name"), sub: t("beratung.o2sub"), desc: t("beratung.o2d"),
    },
    {
      icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>,
      name: t("beratung.o3name"), sub: t("beratung.o3sub"), desc: t("beratung.o3d"),
    },
  ];

  return (
    <div className="fp-root">
      <style>{pageStyles}</style>
      <FpHero eyebrow={t("beratung.eyebrow")} title1={t("beratung.heroTitle1")} title2={t("beratung.heroTitle2")} lead={t("beratung.heroLead")} />

      {/* Why GEO consulting */}
      <div className="fp-section-alt">
        <div className="fp-section-inner">
          <div className="fp-section-label">{t("beratung.whyLabel")}</div>
          <h2 className="fp-section-title">{t("beratung.whyTitle")}</h2>
          <div className="fp-grid-3">
            {[
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 8v4l3 3"/></svg>,
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>,
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>,
            ].map((icon, i) => (
              <div className="fp-card" key={i}>
                <div className="fp-card-icon">{icon}</div>
                <div className="fp-card-title">{t(`beratung.why${i+1}t`)}</div>
                <p className="fp-card-body">{t(`beratung.why${i+1}d`)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Offerings overview */}
      <div className="fp-section">
        <div className="fp-section-label">{t("beratung.offerLabel")}</div>
        <h2 className="fp-section-title">{t("beratung.offerTitle")}</h2>
        <div style={{ display: "flex", flexDirection: "column", gap: 20, marginTop: 32 }}>
          {offerings.map((o, i) => (
            <div key={i} style={{ display: "flex", gap: 24, alignItems: "flex-start", background: "white", border: "1px solid #e2e8f0", borderRadius: 14, padding: "28px 32px", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
              <div style={{ width: 52, height: 52, background: "rgba(37,99,235,0.07)", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {o.icon}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 17, color: "#0f172a", marginBottom: 4 }}>{o.name}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#64748b", letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 10 }}>{o.sub}</div>
                <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.6, margin: 0 }}>{o.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Process */}
      <div className="fp-section-alt">
        <div className="fp-section-inner">
          <div className="fp-section-label">{t("beratung.processLabel")}</div>
          <h2 className="fp-section-title">{t("beratung.processTitle")}</h2>
          <div className="fp-steps" style={{ marginTop: 28 }}>
            {[1,2,3,4].map(i => (
              <div className="fp-step" key={i}>
                <div className="fp-step-num">{i}</div>
                <div>
                  <div className="fp-step-title">{t(`beratung.ps${i}t`)}</div>
                  <div className="fp-step-sub">{t(`beratung.ps${i}d`)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="fp-cta-band" style={{ flexDirection: "column", gap: 16, textAlign: "center" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.6)", letterSpacing: "0.08em", textTransform: "uppercase" }}>{t("beratung.ctaLabel")}</div>
        <h2 style={{ margin: 0, fontFamily: "'new-science','DM Sans',sans-serif", fontSize: "clamp(22px,3vw,32px)" }}>{t("beratung.ctaTitle")}</h2>
        <p style={{ margin: "0 auto", maxWidth: 520, opacity: 0.8, fontSize: 15, lineHeight: 1.6 }}>{t("beratung.ctaSub")}</p>
        <button className="fp-btn-white" onClick={() => setShowModal(true)} style={{ marginTop: 8 }}>{t("beratung.ctaBtn")}</button>
      </div>

      {showModal && <InterestModal plan={t("beratung.ctaTitle")} onClose={() => setShowModal(false)} />}
    </div>
  );
}
